import {createSlice} from '@reduxjs/toolkit';
import {sortVehicleNamesHelper} from '../../app/utils';
import {readCabviewUiCache} from './cabviewUiLocalCache';

// Fast-path hydration: last session's UI prefs paint immediately instead of
// flashing defaults until getInitCabviewData returns (authoritative restore).
const uiCache = readCabviewUiCache();

const cabviewSlice = createSlice({
  name: 'cabview',
  initialState: {
    vehicles: [],
    hazards: [],
    tasks: [],
    implements: [],
    vehicleLabels: [],
    zonesDicts: {},
    geofencesDict: {},
    taskActive: false,
    selectedVehicle: '',
    selectedTask: '',
    selectedImplement: '',
    displayImplements: false,
    displayedPlotLines: [],
    displayClusters: true,
    displayStatusRingsBadges: true,
    machineType: 0,
    reiActiveDict: {},
    refreshCabviewDataStream: false,
    serverTimeOffsetMs: 0, // Date.now() - serverTime, set on each data arrival
    // Default Values for Display Zones Options in Map
    displayZones: true,
    zoneLabelsIndex: 0,
    zoneColorIndex: 0,
    // Left vehicle sidebar (revamp) — additive UI state
    sidebarOpen: false,
    // 'status' = group by driving/idle/off/disconnected; 'alphabetical' = flat A→Z list
    listViewMode: uiCache.listViewMode === 'alphabetical' ? 'alphabetical' : 'status',
    groupStates: {
      driving: true,
      idle: true,
      off: true,
      disconnected: true,
      implements: false,
      vehicles: true,
      ...(uiCache.groupStates || {}),
    },
    searchQuery: '',
    // statuses/labelIds/tasks/operators/vehicles are multi-select arrays;
    // unsafe/badInstall/brokenRules/overdueMaintenance are boolean toggles.
    // brokenRules/overdueMaintenance/labelIds depend on backend fields added in M2 and
    // stay inert until those fields are present on the vehicle payload.
    // labelIds: machine-type pseudo IDs + custom label IDs (combined Vehicle Labels).
    filters: {
      statuses: [],
      tasks: [],
      taskTypes: [],
      operators: [],
      vehicles: [],
      implements: [],
      labelIds: [],
      unsafe: false,
      badInstall: false,
      brokenRules: false,
      overdueMaintenance: false,
      ...(uiCache.filters || {}),
    },
  },
  reducers: {
    updateDisplayedPlotLines(state, action) {
      state.displayedPlotLines = action.payload;
    },
    updateDisplayClusters(state, action) {
      state.displayClusters = action.payload;
    },
    updateDisplayStatusRingsBadges(state, action) {
      state.displayStatusRingsBadges = action.payload;
    },
    updateCabviewVehicles(state, action) {
      const vehDataPack = action.payload;
      const vehDataFiltered = [];

      for (let i = 0; i < vehDataPack.length; i++) {
        // Create deep copy of the payload
        const vehicle = JSON.parse(JSON.stringify(vehDataPack[i]));
        vehDataFiltered.push(vehicle);
      }

      const sortedVehicles = vehDataFiltered.sort((a, b) => {
        return sortVehicleNamesHelper(a.name, b.name);
      });

      state.vehicles = sortedVehicles;
    },
    updateHazards(state, action) {
      const hazardDataPack = action.payload;
      if (hazardDataPack != null || hazardDataPack != undefined) {
        state.hazards = hazardDataPack;
      }
    },
    setHazards(state, action) {
      state.hazards = Array.isArray(action.payload) ? action.payload : [];
    },
    prepareCabviewVehiclesForGpsData(state, action) {
      state.vehicles.forEach((vehicle) => {
        vehicle.gpsCoords = [];
      });
    },
    updateTasks(state, action) {
      const taskDataPack = action.payload;
      const tasks = [];
      const sortedTaskDocs = taskDataPack.sort((a, b) => {
        if (a.name == 'No Task') {
          // `a` is No Task, sort it to front
          return -1;
        } else if (b.name == 'No Task') {
          // `b` is No Task, sort it to back
          return 1;
        } else {
          // both not No Task return alphabetic sort
          return a.name.localeCompare(b.name);
        }
      });
      for (let i = 0; i < sortedTaskDocs.length; i++) {
        // Filter out archived tasks
        if (!sortedTaskDocs[i].archived) {
          tasks.push(sortedTaskDocs[i]);
        }
      }
      state.tasks = tasks;
    },
    updateImplements(state, action) {
      state.implements = action.payload;
    },
    updateVehicleLabels(state, action) {
      const payload = action.payload;
      // Cabview init sends DatabaseStore dict; normalize to array for the filter UI.
      if (Array.isArray(payload)) {
        state.vehicleLabels = payload;
      } else if (payload && typeof payload === 'object') {
        state.vehicleLabels = Object.values(payload);
      } else {
        state.vehicleLabels = [];
      }
    },
    updateGeofences(state, action) {
      state.geofencesDict = action.payload;
    },
    updateZonesData(state, action) {
      const allZoneData = action.payload;

      state.zonesDicts = {
        blocks: allZoneData.blocks,
        fields: allZoneData.fields,
        regions: allZoneData.regions,
      };
    },
    updateReiActive(state, action) {
      const reiList = action.payload;
      const reiActiveDict = {};

      reiList.map((rei) => {
        reiActiveDict[rei.zoneId] = rei;
      });

      state.reiActiveDict = reiActiveDict;
    },
    updateDisplayZonesOptions(state, action) {
      const stateName = action.payload.stateName;
      const value = action.payload.value;
      if (value !== undefined && value !== null) {
        state[stateName] = value;
      }
    },
    setTaskActive(state, action) {
      state.taskActive = action.payload;
    },
    setSelectedVehicle(state, action) {
      state.selectedVehicle = action.payload;
    },
    setSelectedTask(state, action) {
      state.selectedTask = action.payload;
    },
    setSelectedImplement(state, action) {
      state.selectedImplement = action.payload;
    },
    updateDisplayImplements(state, action) {
      state.displayImplements = action.payload;
    },

    // Refreshing the cabview data stream
    setRefreshCabviewDataStream(state, action) {
      state.refreshCabviewDataStream = action.payload;
    },

    // Client-vs-server clock offset, used to evaluate data staleness against the server clock
    updateServerTimeOffset(state, action) {
      const serverTime = action.payload;
      if (typeof serverTime === 'number') {
        state.serverTimeOffsetMs = Date.now() - serverTime;
      }
    },

    // ---- Left vehicle sidebar (revamp) ----
    setSidebarOpen(state, action) {
      state.sidebarOpen = action.payload;
    },
    setListViewMode(state, action) {
      state.listViewMode = action.payload === 'alphabetical' ? 'alphabetical' : 'status';
    },
    setSearchQuery(state, action) {
      state.searchQuery = action.payload;
    },
    setCabviewFilter(state, action) {
      const {key, value} = action.payload;
      state.filters[key] = value;
    },
    setGroupState(state, action) {
      const {key, open} = action.payload;
      state.groupStates[key] = open;
    },
    // Bulk restore of session-persisted UI state on CabView init.
    setGroupStates(state, action) {
      state.groupStates = {...state.groupStates, ...action.payload};
    },
    setCabviewFilters(state, action) {
      state.filters = {...state.filters, ...action.payload};
    },
    // Optimistic in-place task assignment from the sidebar editor; the next
    // data tick reconciles with the persisted task_event.
    updateVehicleTask(state, action) {
      const {serialNumber, taskId, taskName, implementCleared = true} = action.payload;
      const idx = state.vehicles.findIndex((v) => {
        return v.serialNumber === serialNumber;
      });
      if (idx !== -1) {
        state.vehicles[idx] = {
          ...state.vehicles[idx],
          latestTaskId: taskId,
          latestTaskName: taskName,
          implementCleared: implementCleared,
          attachedImplementSN: '',
          attachedImplementName: '',
          attachedImplementFromBluetooth: false,
          latestTaskFromBluetooth: false,
        };
      }
    },
    // Optimistic in-place implement assignment from the sidebar editor; the next
    // data tick reconciles with the persisted implement_event.
    updateVehicleImplement(state, action) {
      const {serialNumber, implementSN, implementName, taskId, taskName} = action.payload;
      const idx = state.vehicles.findIndex((v) => {
        return v.serialNumber === serialNumber;
      });
      if (idx !== -1) {
        const patch = {
          ...state.vehicles[idx],
          attachedImplementSN: implementSN,
          attachedImplementName: implementName,
          implementCleared: !implementSN,
        };
        if (taskId !== undefined && taskId !== null) {
          patch.latestTaskId = taskId;
        }
        if (taskName !== undefined && taskName !== null) {
          patch.latestTaskName = taskName;
        }
        state.vehicles[idx] = patch;
      }
    },
    clearCabviewFilters(state) {
      state.filters = {
        statuses: [],
        tasks: [],
        taskTypes: [],
        operators: [],
        vehicles: [],
        implements: [],
        labelIds: [],
        unsafe: false,
        badInstall: false,
        brokenRules: false,
        overdueMaintenance: false,
      };
      state.searchQuery = '';
    },
  },
});

export const {
  updateCabviewVehicles,
  updateHazards,
  setHazards,
  updateTasks,
  updateImplements,
  updateGeofences,
  updateZonesData,
  updateReiActive,
  setTaskActive,
  setSelectedVehicle,
  setSelectedTask,
  setSelectedImplement,
  updateDisplayImplements,
  prepareCabviewVehiclesForGpsData,
  updateDisplayedPlotLines,
  updateDisplayClusters,
  updateDisplayStatusRingsBadges,
  updateDisplayZonesOptions,
  setRefreshCabviewDataStream,
  updateServerTimeOffset,
  setSidebarOpen,
  setListViewMode,
  setSearchQuery,
  setCabviewFilter,
  setGroupState,
  setGroupStates,
  setCabviewFilters,
  clearCabviewFilters,
  updateVehicleTask,
  updateVehicleImplement,
  updateVehicleLabels,
} = cabviewSlice.actions;
export default cabviewSlice.reducer;
